import React, { useState, useEffect } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { fetchData, deleteData, activateData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import ReactDOM from 'react-dom';
import BillPrint from '../Receipt/Bill';
import CircularProgress from '@mui/material/CircularProgress';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PrintIcon from '@mui/icons-material/Print';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import '../Customer/Customer.css';

function ReceiptIndex() {
    const history = useHistory();
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [data, setData] = useState([]);
    const [deleteItemId, setDeleteItemId] = useState(null);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const { isAuthenticated, companyId, permissions, logout, token } = useAuth();
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }
        setFromDate(getFormattedStartDate());
        setToDate(getFormattedCurrentDate());
        fetchTableData();
    }, [isAuthenticated, companyId, token, logout]);

    const getFormattedCurrentDate = () => {
        const currentDate = new Date();
        return currentDate.toISOString().split('T')[0];
    };

    const getFormattedStartDate = () => {
        const monthStartDate = new Date();
        monthStartDate.setDate(1);
        return monthStartDate.toISOString().split('T')[0];
    };

    const handleFromDateChange = (e) => setFromDate(e.target.value);
    const handleToDateChange = (e) => setToDate(e.target.value);

    const hasCreateTransactionPermission = permissions.includes('Create Transaction');
    const hasEditTransactionPermission = permissions.includes('Edit Transaction');
    const hasDeleteTransactionPermission = permissions.includes('Delete Transaction');

    const fetchTableData = async () => {
        setData([]);
        setLoading(true);
        try {
            const response = await fetchData('ReceiptApi', `/${companyId}/${fromDate}/${toDate}`, token);
            if (response.responseCode === '1111') {
                setData(response.response_data);
            }
        } catch (error) {
            console.error('Error fetching Receipt Details:', error);
            if (error.response && error.response.status === 401) {
                logout();
                history.replace(`/login?redirect=${encodeURIComponent(window.location.pathname)}`);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        deleteData('ReceiptApi', `${deleteItemId}`, token)
            .then(() => {
                fetchTableData();
                setDeleteItemId(null);
                setConfirmDialogOpen(false);
            })
            .catch((error) => {
                console.error('Error deleting record:', error);
            });
    };

    const handleActivate = (itemId) => {
        activateData('ReceiptApi', `${itemId}`, token)
            .then(() => {
                fetchTableData();
            })
            .catch((error) => {
                console.error('Error activating record:', error);
            });
    };

    const openConfirmDialog = (itemId) => {
        setDeleteItemId(itemId);
        setConfirmDialogOpen(true);
    };

    const closeConfirmDialog = () => {
        setDeleteItemId(null);
        setConfirmDialogOpen(false);
    };

    function formatDate(dateString) {
        const options = { year: 'numeric', month: '2-digit', day: '2-digit' };
        return new Date(dateString).toLocaleDateString('en-GB', options);
    }

    const handlePrint = async (saleid) => {
        try {
            const response = await fetchData('ReceiptApi', `/ReceiptPrint/${saleid}`, token);
            const labelData = response.response_data;

            const iframe = document.createElement('iframe');
            iframe.style.display = 'none';
            document.body.appendChild(iframe);

            const iframeDocument = iframe.contentDocument || iframe.contentWindow.document;
            iframeDocument.write('<html><head><style>@media print { header, footer { display: none !important; } }</style></head><body>');

            ReactDOM.render(
                <BillPrint labelData={labelData} onClose={() => document.body.removeChild(iframe)} />,
                iframeDocument.body
            );

            iframeDocument.write('</body></html>');
            iframeDocument.close();
            setTimeout(() => {
                iframe.contentWindow.print();
            }, 1000);
        } catch (error) {
            console.error('Error printing Invoice:', error);
        }
    };

    const handleSearch = (event) => setSearchQuery(event.target.value);

    const filteredDataItem = data.filter((item) =>
        Object.values(item).some((value) =>
            String(value).toLowerCase().includes(searchQuery.toLowerCase())
        )
    );

    return (
        <div className="customer-container">
            {/* Loading Overlay */}
            {loading && (
                <div className="loading-overlay">
                    <CircularProgress className="loading-spinner" />
                    <span className="loading-text">Loading...</span>
                </div>
            )}

            {/* Page Header */}
            <div className="page-header">
                <h1 className="page-title">Rent Receipt</h1>
                <div className="header-actions">
                    {/* Search */}
                    <div className="search-box">
                        <SearchIcon />
                        <input
                            type="text"
                            className="search-input"
                            placeholder="Search..."
                            value={searchQuery}
                            onChange={handleSearch}
                        />
                    </div>

                    {/* Date Filters */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <label style={{ fontSize: '11px', fontWeight: '600', color: '#475569', marginBottom: '2px' }}>From</label>
                            <input
                                type="date"
                                value={fromDate}
                                onChange={handleFromDateChange}
                                className="form-input"
                                style={{ padding: '6px 10px', fontSize: '13px' }}
                            />
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <label style={{ fontSize: '11px', fontWeight: '600', color: '#475569', marginBottom: '2px' }}>To</label>
                            <input
                                type="date"
                                value={toDate}
                                onChange={handleToDateChange}
                                className="form-input"
                                style={{ padding: '6px 10px', fontSize: '13px' }}
                            />
                        </div>
                    </div>

                    {/* Refresh Button */}
                    <button className="create-btn" onClick={fetchTableData} style={{ background: '#059669' }}>
                        <RefreshIcon fontSize="small" />
                        Refresh
                    </button>

                    {/* Create Button */}
                    {hasCreateTransactionPermission && (
                        <Link to="/ReceiptCreate" className="create-btn">
                            <AddIcon fontSize="small" />
                            Create
                        </Link>
                    )}
                </div>
            </div>

            {/* Table */}
            <div className="table-container">
                <div className="table-wrapper">
                    <table className="customer-table">
                        <thead>
                            <tr>
                                <th>Bill Date</th>
                                <th>Bill No</th>
                                <th>Customer</th>
                                <th>Room No</th>
                                <th style={{ textAlign: 'right' }}>Amount</th>
                                <th style={{ textAlign: 'center' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredDataItem.length > 0 ? (
                                filteredDataItem.map((item) => (
                                    <tr key={item.Id} style={{ opacity: item.IsActive ? 1 : 0.5 }}>
                                        <td className="cell-date">{formatDate(item.BillDate)}</td>
                                        <td><span className="cell-room">{item.BillNo}</span></td>
                                        <td className="cell-name">{item.CustomerName}</td>
                                        <td>{item.RoomName}</td>
                                        <td style={{ textAlign: 'right', fontWeight: '600', color: '#059669' }}>
                                            ₹{item.Amount.toFixed(2)}
                                        </td>
                                        <td>
                                            <div className="action-buttons" style={{ justifyContent: 'center' }}>
                                                {hasEditTransactionPermission && item.IsActive && (
                                                    <Link to={`/ReceiptEdit/${item.Id}`}>
                                                        <button className="action-btn edit">
                                                            <EditIcon fontSize="small" />
                                                        </button>
                                                    </Link>
                                                )}
                                                {hasDeleteTransactionPermission && (
                                                    <button
                                                        className={`action-btn ${item.IsActive ? 'delete' : 'edit'}`}
                                                        onClick={() => item.IsActive ? openConfirmDialog(item.Id) : handleActivate(item.Id)}
                                                    >
                                                        {item.IsActive ? <DeleteIcon fontSize="small" /> : <CheckCircleIcon fontSize="small" />}
                                                    </button>
                                                )}
                                                <button className="action-btn print" onClick={() => handlePrint(item.Id)}>
                                                    <PrintIcon fontSize="small" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="6">
                                        <div className="empty-state">
                                            <p>No receipts found</p>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Delete Confirmation Dialog */}
            {confirmDialogOpen && (
                <div className="dialog-overlay">
                    <div className="dialog-box">
                        <h3 className="dialog-title">Delete Receipt?</h3>
                        <p className="dialog-message">Are you sure you want to delete this receipt? This action cannot be undone.</p>
                        <div className="dialog-actions">
                            <button className="dialog-btn cancel" onClick={closeConfirmDialog}>Cancel</button>
                            <button className="dialog-btn confirm" onClick={handleDelete}>Delete</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default ReceiptIndex;
